import { Anchor, Checkbox, Stack, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { ModalFooter } from 'components/ModalFooter';
import { i18nKeys } from 'locales/index';
import { useTranslation } from 'locales/logic';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { first } from 'remeda';
import { useSendToThirdParty } from 'shared/hooks/use-send-to-third-party';
import { useGetInvoiceState } from 'shared/utils/selectors';
import { invoiceActions } from 'store/invoice/invoice.actions';
import type { Id } from 'types';

const LEGALCITY_TERMS_URL = 'https://recovr.s3.eu-west-3.amazonaws.com/CGV/cgv_legalcity.pdf';

type LegalCityModalProps = {
  invoiceIds: Array<Id>;
  legalcityId: string;
  additionalInvoiceIds?: Array<Id>;
};

export const LegalCityModal = ({
  invoiceIds,
  legalcityId,
  additionalInvoiceIds = [],
}: LegalCityModalProps) => {
  const history = useHistory();
  const { t } = useTranslation();

  const { sendToThirdParty, isSendToThirdPartyLoading } = useSendToThirdParty();
  const { isLoading: isBatchActionLoading } = useGetInvoiceState();

  const [termsAccepted, setTermsAccepted] = useState(false);

  const openSingleCase = () => {
    const options = {
      id: String(first(invoiceIds)!),
      data: {
        third_party_company_id: String(legalcityId),
        additional_invoices: additionalInvoiceIds,
      },
    };
    sendToThirdParty(options, {
      onSuccess: () => {
        history.push('/thirdparty-cases');
        modals.closeAll();
      },
    });
  };

  const openBatchCase = () => {
    modals.closeAll();
    invoiceActions.actionsBatchAction({
      data: {
        third_party_company_id: legalcityId,
        action_type: 'third_party',
        invoice_ids: invoiceIds,
      },
    });
  };

  const isBatch = invoiceIds.length > 1;
  const openCase = isBatch ? openBatchCase : openSingleCase;
  const isLoading = isBatch ? isBatchActionLoading : isSendToThirdPartyLoading;

  return (
    <Stack gap="md">
      <Text size="sm">{t(i18nKeys.FORM.CONFIRM_LEGALCITY.PRICING_INFO)}</Text>
      <Checkbox
        checked={termsAccepted}
        onChange={event => setTermsAccepted(event.currentTarget.checked)}
        label={
          <Text size="sm">
            {t(i18nKeys.FORM.CONFIRM_LEGALCITY.READ_AND_APPROVE)}{' '}
            <Anchor href={LEGALCITY_TERMS_URL} target="_blank" rel="noopener noreferrer">
              {t(i18nKeys.FORM.GENERAL_CONDITIONS_OF_USE)}
            </Anchor>
          </Text>
        }
      />
      <ModalFooter
        onSave={openCase}
        isLoading={isLoading}
        isDisabled={!termsAccepted}
        onSaveProps={{ children: t(i18nKeys.CONFIRM) }}
      />
    </Stack>
  );
};
